/**
 * A client-side *mirror* of the independence and eligibility gates, for live
 * feedback while the CPA fills in Step 4 and for the Step 5 blockers list.
 *
 * This deliberately breaks the rule stated in `formState.ts` — that the wizard
 * does not decide what the engine decides — so it is worth being explicit about
 * why, and about the limits.
 *
 * **Why.** Every gate currently fires at the Build call, so the CPA completes
 * all five steps before learning that the box on Step 4 was the problem. The
 * independence gate is the one gate that is pure boolean logic plus a name
 * comparison: no policy lookup, no model call, no server state. Mirroring it
 * costs nothing and turns a five-step round trip into an inline red/green.
 *
 * **The limits, which the callers honour.**
 *  - This is *advisory*. It never blocks a Build and never fabricates a
 *    refusal. The server's answer is the only one that is recorded, and the
 *    server may refuse for reasons this file cannot see.
 *  - It mirrors what the *request* will contain, not what the form holds — so
 *    it applies the same drop of blank-entity/blank-type interests that
 *    `buildLetterRequest` applies. Showing the CPA a block for an interest that
 *    is never transmitted would be a lie in the other direction.
 *  - The eligibility half is partial by construction: licence *states* live on
 *    the firm profile and are resolved server-side, so a wrong-state engagement
 *    can only be caught at Build. `eligibilityPartial` says so out loud rather
 *    than letting a green tick imply more than it knows.
 *
 * Ported line-for-line from `core/attest/independence.py`. If that file
 * changes, this one is a required edit — `tests/test_attest_independence_matrix.py`
 * is the matrix both are held to.
 */
import type { IssuerFormState } from '@/components/attest/formState'

/** `_ENTITY_SUFFIXES` in `core/attest/independence.py`. */
const ENTITY_SUFFIXES = new Set([
  'llc', 'l.l.c', 'inc', 'incorporated', 'corp', 'corporation', 'ltd',
  'limited', 'lp', 'llp', 'plc', 'co', 'company', 'pllc', 'pc',
])

/** `normalize_entity` — case, punctuation and legal-suffix insensitive. */
export function normalizeEntity(name: string): string {
  const cleaned = (name || '').toLowerCase().replace(/[^\w\s]/g, ' ')
  return cleaned
    .split(/\s+/)
    .filter(t => t && !ENTITY_SUFFIXES.has(t))
    .join(' ')
}

/**
 * `entities_match`. Substring on purpose — it is what the engine does today.
 * It over-matches on short tokens (finding IND-SUBSTRING-OVERMATCH), and this
 * mirror reproduces that rather than quietly being smarter than the gate it
 * is previewing.
 */
export function entitiesMatch(a: string, b: string): boolean {
  const na = normalizeEntity(a)
  const nb = normalizeEntity(b)
  if (!na || !nb) return false
  return na === nb || na.includes(nb) || nb.includes(na)
}

export interface PreflightImpairment {
  code: string
  reason: string
}

export interface IndependencePreflight {
  independent: boolean
  impairments: PreflightImpairment[]
  /** True once the subject entity is known — before that, nothing is decidable. */
  evaluated: boolean
}

/** Mirror of `evaluate_independence`. */
export function previewIndependence(
  issuer: IssuerFormState,
  subjectEntity: string,
): IndependencePreflight {
  const impairments: PreflightImpairment[] = []

  if (issuer.isPlatformBackOffice) {
    impairments.push({
      code: 'PLATFORM_BACK_OFFICE_CANNOT_ISSUE',
      reason:
        'The platform’s back-office firm is never the issuer of an attest-type letter.',
    })
  }

  // Same filter `buildLetterRequest` applies — a half-typed row is not yet a
  // declaration, and the server will never see it.
  const declared = issuer.declaredInterests.filter(
    i => i.subject_entity.trim() && i.interest_type.trim(),
  )
  const conflicting = declared.filter(i =>
    entitiesMatch(i.subject_entity, subjectEntity),
  )

  for (const interest of conflicting) {
    impairments.push({
      code: 'FINANCIAL_INTEREST',
      reason: `An interest of type “${interest.interest_type}” in ${interest.subject_entity} is on record.`,
    })
  }

  if (conflicting.length && issuer.attestedNoImpairingInterest) {
    impairments.push({
      code: 'ATTESTATION_CONTRADICTED',
      reason:
        'The firm attested to no impairing interest, but an interest in the subject entity is declared.',
    })
  }

  if (!issuer.attestedNoImpairingInterest && !conflicting.length) {
    impairments.push({
      code: 'NO_INDEPENDENCE_ATTESTATION',
      reason:
        'The issuer has not attested that it holds no impairing financial interest in the subject entity.',
    })
  }

  return {
    independent: impairments.length === 0,
    impairments,
    evaluated: Boolean(subjectEntity.trim()),
  }
}

export interface EligibilityPreflight {
  eligible: boolean
  problems: string[]
  /**
   * True only when the eligibility check actually ran: licence states are
   * resolved from the firm profile on the server, so a wrong-state engagement
   * is invisible from here even when the rest of the check applies. False
   * when the check did not run at all (`requiresAttestEligibleIssuer` is
   * false) — there is nothing to be partial about in that case.
   */
  partial: boolean
}

/**
 * Partial mirror of `check_issuer_eligibility` — see `partial`.
 *
 * PREFLIGHT-BASIS-AWARE-1: `check_issuer_eligibility` on the server returns
 * immediately, with zero problems, when the engagement type does not require
 * an attest-eligible issuer (`core/attest/independence.py`, the
 * `requires_attest_eligible_issuer` guard before any of licence/peer-review/
 * expiry is even inspected). This mirror used to skip that guard and check
 * unconditionally, so it could report "would block this letter" for an
 * engagement the server was always going to build successfully — a false
 * warning the CPA had no way to resolve, because there was nothing wrong.
 * `requiresAttestEligibleIssuer` must be
 * `engagementRequiresAttestEligibleIssuer(letterType, policy)` from
 * `requiredFields.tsx` — the same derivation the Step-4 stars use, so this
 * can never fall out of sync with them the way it did before.
 */
export function previewEligibility(
  issuer: IssuerFormState,
  requiresAttestEligibleIssuer: boolean,
  today = new Date(),
): EligibilityPreflight {
  if (!requiresAttestEligibleIssuer) {
    return { eligible: true, problems: [], partial: false }
  }
  const problems: string[] = []
  if (!issuer.licensedForAttest) {
    problems.push('firm is not licensed to perform attest engagements')
  }
  if (!issuer.peerReviewCurrent) {
    problems.push('firm has no current peer review')
  }
  const expiry = issuer.peerReviewExpiry.trim()
  if (expiry) {
    // Compare as plain dates — the field is a `<input type="date">` value, and
    // parsing it as an instant would shift it by the viewer's timezone.
    const todayIso = [
      today.getFullYear(),
      String(today.getMonth() + 1).padStart(2, '0'),
      String(today.getDate()).padStart(2, '0'),
    ].join('-')
    if (expiry < todayIso) problems.push(`peer review expired ${expiry}`)
  }
  return { eligible: problems.length === 0, problems, partial: true }
}
